// apps/web/src/admin/AdminRevenueView.tsx
import { useEffect, useState } from 'react';
import type { AdminRevenueResponse } from '@aslgo/shared';
import { apiAdmin } from './apiAdmin.js';
import { ExportPdfButton } from './ExportPdfButton.js';
import { reportTimestamp, withinLast30Days } from './reportWindow.js';

/** Round a raw max up to a tidy axis ceiling (nearest $20, min $20) so the four
 *  quarter gridlines land on round dollar values. */
function axisMax(raw: number): number {
  return Math.max(20, Math.ceil(raw / 20) * 20);
}

/** "Jun 08" in UTC — date strings are date-only ISO, i.e. UTC midnight. */
function tickLabel(date: string): string {
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return date;
  return d.toLocaleDateString('en-US', { month: 'short', day: '2-digit', timeZone: 'UTC' });
}

export function AdminRevenueView() {
  const [data, setData] = useState<AdminRevenueResponse | null>(null);
  useEffect(() => { void (async () => setData(await apiAdmin.revenue()))(); }, []);
  if (!data) return <p className="text-ink-500">Loading…</p>;

  const days = data.dailyRevenueLast30;
  const max = axisMax(Math.max(1, ...days.map((d) => d.dollars)));
  const yTicks = [max, (max * 3) / 4, max / 2, max / 4, 0];
  const total = days.reduce((sum, d) => sum + d.dollars, 0);
  const lastIdx = days.length - 1;

  const exportPdf = async () => {
    const { generateReportPdf } = await import('./reportPdf.js');
    const now = new Date();
    const recent = data.recentEvents.filter((e) => withinLast30Days(e.createdAt, now));
    generateReportPdf({
      title: 'Revenue Report',
      subtitle: `Last 30 days · Generated ${reportTimestamp(now)}`,
      summary: [
        ['30-day revenue total', `$${total}`],
        ['Top-ups today', `$${data.topupsTodayDollars} (${data.topupsTodayCount})`],
        ['Top-ups MTD', `$${data.topupsMtdDollars} (${data.topupsMtdCount})`],
        ['Top-ups all-time', `$${data.topupsAllDollars} (${data.topupsAllCount})`],
        ['Minutes today / MTD / all', `${data.minutesTodayCount} / ${data.minutesMtdCount} / ${data.minutesAllCount}`],
        ['Refunds & adjustments MTD', `${data.refundsMtdCount} events (${data.refundsMtdMinutes} min)`],
      ],
      tables: [
        { heading: 'Daily revenue', columns: ['Date', 'Revenue ($)'], rows: days.map((d) => [d.date, d.dollars]) },
        {
          heading: 'Recent activity (last 30 days)',
          columns: ['Time', 'User', 'Kind', 'Δ min', '$'],
          rows: recent.map((e) => [
            new Date(e.createdAt).toISOString().replace('T', ' ').slice(0, 19),
            e.userEmail,
            e.count > 1 ? `${e.kind} ×${e.count}` : e.kind,
            e.minutesDelta,
            e.dollars ?? '',
          ]),
        },
      ],
      filename: 'revenue-last-30-days.pdf',
    });
  };

  const stats: { label: string; value: string; sub?: string }[] = [
    { label: 'Top-ups today', value: `$${data.topupsTodayDollars}`, sub: `${data.topupsTodayCount} charges` },
    { label: 'Top-ups MTD',   value: `$${data.topupsMtdDollars}`,   sub: `${data.topupsMtdCount} charges` },
    { label: 'Top-ups all',   value: `$${data.topupsAllDollars}`,   sub: `${data.topupsAllCount} charges` },
    { label: 'Minutes today', value: `${data.minutesTodayCount}` },
    { label: 'Minutes MTD',   value: `${data.minutesMtdCount}` },
    { label: 'Minutes all',   value: `${data.minutesAllCount}` },
  ];

  return (
    <div className="space-y-7">
      <div className="flex items-center justify-between">
        <p className="text-xs text-ink-500 dark:text-[#b6b2c4]">All figures in UTC.</p>
        <ExportPdfButton onExport={exportPdf} />
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {stats.map((s) => (
          <div
            key={s.label}
            className="rounded-xl border border-ink-200/70 dark:border-white/[0.07] bg-surface-light dark:bg-surface-darkAlt p-5"
          >
            <div className="text-[0.82rem] text-ink-600 dark:text-[#b6b2c4] mb-2.5">{s.label}</div>
            <div className="text-2xl font-semibold tabular-nums text-ink-900 dark:text-ink-100">
              {s.value}
              {s.sub ? (
                <span className="text-[0.95rem] font-medium text-ink-500 dark:text-[#b6b2c4]"> · {s.sub}</span>
              ) : null}
            </div>
          </div>
        ))}
      </div>
      <p className="text-xs text-ink-500 dark:text-[#b6b2c4]">
        Refunds &amp; adjustments MTD: {data.refundsMtdCount} events ({data.refundsMtdMinutes} minutes)
      </p>

      {/* Daily revenue chart */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-ink-900 dark:text-ink-100">Daily revenue (last {days.length} days)</h2>
          <span className="text-xs text-ink-500 dark:text-[#b6b2c4]">Total ${total}</span>
        </div>
        <div className="rounded-xl border border-ink-200/70 dark:border-white/[0.07] bg-surface-light dark:bg-surface-darkAlt p-5 pb-3">
          <div className="flex gap-3 h-56">
            {/* y axis */}
            <div className="flex flex-col justify-between text-right text-[0.7rem] text-ink-400 dark:text-[#7c7889] pb-6 shrink-0">
              {yTicks.map((t, i) => <span key={i}>${Math.round(t)}</span>)}
            </div>
            {/* plot */}
            <div className="relative flex-1 min-w-0">
              {/* gridlines (above the date-tick band) */}
              <div className="absolute left-0 right-0 top-0 bottom-6">
                {[0, 25, 50, 75].map((p) => (
                  <div key={p} style={{ top: `${p}%` }} className="absolute left-0 right-0 border-t border-ink-200/70 dark:border-white/[0.06]" />
                ))}
                <div className="absolute left-0 right-0 bottom-0 border-t border-ink-300 dark:border-white/[0.12]" />
              </div>
              {/* bars */}
              <div className="absolute inset-0 flex items-end gap-[3px]">
                {days.map((d, i) => {
                  const pct = d.dollars > 0 ? Math.max((d.dollars / max) * 100, 3) : 0;
                  const showTick = i % 5 === 0 || i === lastIdx;
                  return (
                    <div key={d.date} className="flex-1 h-full flex flex-col items-center justify-end gap-1 min-w-0">
                      <span className="h-3.5 text-[0.6rem] tabular-nums text-ink-500 dark:text-[#b6b2c4]">
                        {d.dollars > 0 ? `$${d.dollars}` : ''}
                      </span>
                      <div
                        title={`${tickLabel(d.date)} — $${d.dollars}`}
                        style={{ height: `${pct}%` }}
                        className={`w-[60%] max-w-[26px] rounded-t-sm ${d.dollars > 0 ? 'bg-brand dark:bg-brand-bright' : 'bg-ink-200 dark:bg-white/[0.07]'}`}
                      />
                      <span className="h-6 flex items-start justify-center w-full">
                        {showTick ? (
                          <span className="text-[0.55rem] text-ink-400 dark:text-[#7c7889] -rotate-45 origin-top whitespace-nowrap mt-1">
                            {tickLabel(d.date)}
                          </span>
                        ) : null}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent activity */}
      <div>
        <h2 className="text-sm font-semibold text-ink-900 dark:text-ink-100 mb-3">Recent activity</h2>
        <div className="rounded-xl border border-ink-200/70 dark:border-white/[0.07] bg-surface-light dark:bg-surface-darkAlt overflow-hidden">
          <div className="grid grid-cols-[1.6fr_1.4fr_1fr_0.7fr_0.7fr] gap-4 px-5 py-3 border-b border-ink-200/70 dark:border-white/[0.08]
                          text-[0.65rem] uppercase tracking-[0.08em] text-ink-500 dark:text-[#7c7889]">
            <span>Time</span>
            <span>User</span>
            <span>Kind</span>
            <span className="text-right">Δ min</span>
            <span className="text-right">$</span>
          </div>
          {data.recentEvents.map((e) => {
            const tsLast  = new Date(e.createdAt).toISOString().replace('T', ' ').slice(0, 19);
            const tsFirst = new Date(e.firstAt).toISOString().replace('T', ' ').slice(0, 19);
            const time    = e.count > 1 ? `${tsFirst} → ${tsLast}` : tsLast;
            const kind    = e.count > 1 ? `${e.kind} ×${e.count}` : e.kind;
            const isCharge = e.kind === 'charge';
            const isAdjust = e.kind === 'adjust';
            const chipTone = isCharge
              ? 'bg-emerald-50 text-emerald-700 dark:bg-status-successBright/14 dark:text-status-successBright'
              : isAdjust
                ? 'bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300'
                : 'bg-ink-100 text-ink-600 dark:bg-white/[0.07] dark:text-[#b6b2c4]';
            const minTone = e.minutesDelta > 0
              ? 'text-emerald-700 dark:text-status-successBright'
              : e.minutesDelta < 0
                ? 'text-rose-600 dark:text-[#e07e6f]'
                : 'text-ink-500 dark:text-[#b6b2c4]';
            return (
              <div
                key={e.id}
                className="grid grid-cols-[1.6fr_1.4fr_1fr_0.7fr_0.7fr] gap-4 px-5 py-3 items-center
                           border-b border-ink-100 dark:border-white/[0.05] last:border-0 text-[0.8rem]"
              >
                <span className="font-mono text-[0.72rem] text-ink-500 dark:text-[#b6b2c4]">{time}</span>
                <span className="truncate text-ink-700 dark:text-ink-200" title={e.userEmail}>{e.userEmail}</span>
                <span className="min-w-0">
                  <span className={`inline-block rounded-md px-2 py-0.5 text-[0.72rem] font-medium ${chipTone}`}>{kind}</span>
                  {e.note ? (
                    <span className="block mt-0.5 text-[0.68rem] text-ink-400 dark:text-[#7c7889] truncate" title={e.note}>{e.note}</span>
                  ) : null}
                </span>
                <span className={`text-right tabular-nums font-medium ${minTone}`}>
                  {e.minutesDelta > 0 ? `+${e.minutesDelta}` : e.minutesDelta}
                </span>
                <span className="text-right tabular-nums font-semibold text-emerald-700 dark:text-status-successBright">
                  {e.dollars != null ? `$${e.dollars}` : ''}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
